import type { DynamicDataExtraction } from '~/features/dynamic-data-extraction/types/DynamicDataExtraction';
import type { ExtractionAiFieldValues } from '~/features/dynamic-data-extraction/types/ExtractionAiFieldCell';

import { migrateLegacyAiFieldValues } from '~/features/dynamic-data-extraction/utils/migrateLegacyAiFieldValues';
import { normalizeAiFieldCells } from '~/features/dynamic-data-extraction/utils/normalizeAiFieldCells';

const STORAGE_KEY = 'dynamic-data-extraction-items';

type LegacyDynamicDataExtraction = DynamicDataExtraction & {
  aiFieldValues?: ExtractionAiFieldValues;
};

const normalizeExtraction = (
  item: LegacyDynamicDataExtraction,
): DynamicDataExtraction => ({
  ...item,
  aiFieldCells: normalizeAiFieldCells(
    item.aiFieldCells ?? migrateLegacyAiFieldValues(item.aiFieldValues) ?? {},
  ),
  documents: item.documents.map((document) => {
    const documentHandlerId = document.documentHandlerId ?? document.id;

    return {
      ...document,
      documentHandlerId,
      id: documentHandlerId,
    };
  }),
});

export const loadDynamicDataExtractions = (): Record<
  string,
  DynamicDataExtraction
> => {
  if (typeof window === 'undefined') {
    return {};
  }

  try {
    const storedValue = sessionStorage.getItem(STORAGE_KEY);

    if (!storedValue) {
      return {};
    }

    const parsed = JSON.parse(storedValue) as Record<
      string,
      LegacyDynamicDataExtraction
    >;

    return Object.fromEntries(
      Object.entries(parsed ?? {}).map(([id, item]) => [
        id,
        normalizeExtraction({
          ...item,
          aiFields: item.aiFields ?? [],
        }),
      ]),
    );
  } catch {
    return {};
  }
};

export const persistDynamicDataExtractions = (
  items: Record<string, DynamicDataExtraction>,
) => {
  if (typeof window === 'undefined') {
    return;
  }

  const normalizedItems = Object.fromEntries(
    Object.entries(items).map(([id, item]) => [
      id,
      {
        ...item,
        aiFieldCells: normalizeAiFieldCells(item.aiFieldCells ?? {}),
      },
    ]),
  );

  sessionStorage.setItem(STORAGE_KEY, JSON.stringify(normalizedItems));
};
